import { useLocation, useNavigate, useParams, Navigate } from "react-router";
import AppPage from "layouts/AppPage";
import {
  Box,
  Center,
  Divider,
  HStack,
  Icon,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "@chakra-ui/react";
import { LuLayoutDashboard, LuClock } from "react-icons/lu";
import CaminoTaskIcon from "design-system/icons/Task";
import CaminoMeetingIcon from "design-system/icons/Meeting";
import React, { useEffect, useMemo } from "react";
import { useNavbar } from "hooks/useNavbar";
import { useNewHireJourneyDetailQuery } from "features/NewHireJourney/hooks";
import { PeopleTeamPendingNewHireAlertBanner } from "components/AlertBanner/PendingNewHireAlertBanner";
import internalTeam from "definitions/constants/routes/shared/internalTeam";
import CaminoBreadcrumbs from "components/Breadcrumbs";
import OverviewTab from "features/NewHireJourney/Components/Tabs/OverviewTab";
import TasksTab from "features/NewHireJourney/Components/Tabs/TasksTab";
import MeetingsTab from "features/NewHireJourney/Components/Tabs/MeetingsTab";
import ScheduleTab from "features/NewHireJourney/Components/Tabs/ScheduleTab";
import ManagerNewHireHeader from "features/NewHireJourney/Components/NewHireHeader/ManagerNewHireHeader";
import { useNewHireJourneyTasksQuery } from "features/Resources/Task/hooks";
import { NewHireJourneyId } from "models/newHire";
import { groupTasksByUser } from "features/Resources/Task/helpers/filter";
import { useCurrentUserQuery } from "features/User/hooks";
import LinkButton from "components/Button/LinkButton";
import { pendingNewHirePath } from "helpers/url/pages/v1";
import { SHARED_ROUTES } from "definitions/constants/routeConstants";
import ROLE from "models/role";
import TaskActionModalByQueryParam from "features/Resources/Task/Components/Action/TaskActionModalByQueryParam";

function NewHirePage() {
  const { id } = useParams<{ id: string }>();
  const { setBackLocationPath, setBackLocationName, setPageTitle } = useNavbar();
  const location = useLocation();
  const navigate = useNavigate();

  const { data, isLoading, isFetched } = useNewHireJourneyDetailQuery(id || "");

  const { data: currentUser } = useCurrentUserQuery();

  const { data: tasks } = useNewHireJourneyTasksQuery(id as NewHireJourneyId, {
    enabled: !!id,
  });

  const tasksByUser = useMemo(
    () =>
      groupTasksByUser(tasks, {
        newHire: data?.newHire,
        manager: data?.manager,
        my: currentUser,
      }),
    [currentUser, data?.manager, data?.newHire, tasks],
  );

  const teamTasks = useMemo(() => {
    const assignedUserIds = [data?.newHire?.id, data?.manager?.id].filter(Boolean);
    return (tasks ?? []).filter((task) => !assignedUserIds.includes(task.assigneeId));
  }, [data?.manager, data?.newHire, tasks]);

  useEffect(() => {
    if (data?.newHire?.fullName && !isLoading) {
      setPageTitle(`New Hire ${data?.newHire?.fullName ? data.newHire.fullName : ""}`);
    }
    // setBackLocationPath(newHireJourneysPath());
    // setBackLocationName("New Hires");
    return () => {
      setPageTitle(null);
      setBackLocationName(null);
      setBackLocationPath(null);
    };
  }, [data?.newHire?.fullName, isLoading, setBackLocationName, setBackLocationPath, setPageTitle]);

  const memoizedTabs = useMemo(
    () => [
      {
        path: "overview",
        label: "Overview",
        icon: LuLayoutDashboard,
        content: <OverviewTab newHireJourney={data} allTasks={tasks} />,
      },
      {
        path: "tasks",
        label: "Tasks",
        icon: CaminoTaskIcon,
        content: (
          <TasksTab
            newHireJourney={data}
            allTasks={tasks}
            myTasks={tasksByUser.my}
            managerTasks={tasksByUser.manager}
            newHireTasks={tasksByUser.newHire}
            teamTasks={teamTasks}
          />
        ),
      },
      {
        path: "meetings",
        label: "Meetings",
        icon: CaminoMeetingIcon,
        content: <MeetingsTab newHireJourney={data} />,
      },
      {
        path: "schedule",
        label: "Schedule",
        icon: LuClock,
        content: <ScheduleTab newHireJourney={data} />,
      },
    ],
    [data, tasks, tasksByUser.my, tasksByUser.manager, tasksByUser.newHire, teamTasks],
  );

  // Extract the current tab from the URL - look for the segment right after the ID
  const pathSegments = location.pathname.split("/").filter(Boolean);
  // Find the tab name right after the id
  const idIndex = pathSegments.findIndex((segment) => segment === id);
  const activeTabPath = idIndex !== -1 ? pathSegments[idIndex + 1] : pathSegments.at(-1);

  // Find the index of the active tab
  const activeTabIndex = memoizedTabs.findIndex((tab) => tab.path === activeTabPath);

  const isPeopleTeamOrAdmin = currentUser?.permission?.some((r) =>
    ([ROLE.PEOPLE_TEAM, ROLE.COMPANY_ADMIN] as string[]).includes(r),
  );

  useEffect(() => {
    if (activeTabIndex === -1 && memoizedTabs.length > 0) {
      // Default to overview tab
      navigate(`/new-hires/${id}/overview`, { replace: true });
    }
  }, [activeTabIndex, navigate, id, memoizedTabs]);

  // Handle tab change to navigate to the correct route
  const handleTabChange = (index: number) => {
    navigate(`/new-hires/${id}/${memoizedTabs[index].path}`);
  };

  if (isFetched && currentUser && data?.newHire?.id === currentUser.id && !isPeopleTeamOrAdmin) {
    return <Navigate to={SHARED_ROUTES.newHirePortal} replace />;
  }

  return (
    <AppPage>
      <CaminoBreadcrumbs
        breadcrumbInfos={[
          { name: "New Hire Journeys", toUrl: internalTeam.newHireJourneys.index },
          {
            name: isFetched && data?.newHire.fullName ? `${data.newHire.fullName}` : "Onboarding",
          },
        ]}
      />
      {data?.isPending && <PeopleTeamPendingNewHireAlertBanner newHireId={id as string} />}
      <Box my="2">
        <ManagerNewHireHeader newHireJourney={data} tasks={tasks} canEditDetails={isPeopleTeamOrAdmin} />
      </Box>
      {data?.isPending && (
        <>
          <Divider mt="4" />
          <LinkButton to={pendingNewHirePath(data)} variant="alternative" size="sm">
            Kick Off Onboarding
          </LinkButton>
        </>
      )}

      <Tabs index={activeTabIndex} onChange={handleTabChange} mt="4" isLazy>
        <TabList borderBottom="1px solid" borderColor="gray.200" gap="0">
          {memoizedTabs.map(({ path, label, icon: TabIcon }) => (
            <Tab
              key={path}
              _selected={{
                color: "content.primary",
                fontWeight: "600",
                borderBottom: "2px solid",
                borderColor: "content.primary",
                mb: "-1px",
              }}
              _hover={{
                color: "content.primary",
              }}
              color="content.tertiary"
              fontSize="sm"
              fontWeight="500"
              bg="transparent"
              border="none"
              borderRadius="0"
              px="4"
              py="2.5"
            >
              <HStack spacing="1.5">
                <Icon as={TabIcon} boxSize="4" />
                <span>{label}</span>
              </HStack>
            </Tab>
          ))}
        </TabList>
        <TabPanels mt="4">
          {memoizedTabs.map(({ path, content }) => (
            <TabPanel key={path} px="0">
              {data ? (
                content
              ) : (
                <Center>
                  <Spinner thickness="4px" size="xl" />
                </Center>
              )}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
      <TaskActionModalByQueryParam />
    </AppPage>
  );
}

export default React.memo(NewHirePage);
